import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { LandCategory } from '@mtg-deck-tool/shared';

// ─── Default Preferences ──────────────────────────────────────────────────────

/**
 * Categories enabled by default on first use.
 * These are the "good value" cycles most players run — premium and mid-tier only.
 * Everything else is opt-in. ABU duals intentionally excluded (price barrier).
 */
const DEFAULT_ENABLED_CATEGORIES: LandCategory[] = [
  'fetch-on',
  'fetch-off',
  'shock',
  'surveil',
  'triome',
  'verge',
  'rainbow',
];

const DEFAULT_LAND_COUNT = 36;

// ─── Store Interface ──────────────────────────────────────────────────────────

interface LandPrefsState {
  enabledCategories: LandCategory[];
  lastLandCount: number;
  /** Persisted staple land selections (card names). */
  selectedStaples: string[];

  setEnabledCategories: (cats: LandCategory[]) => void;
  toggleCategory: (cat: LandCategory) => void;
  setLastLandCount: (n: number) => void;
  setSelectedStaples: (names: string[]) => void;
}

// ─── Store ────────────────────────────────────────────────────────────────────

export const useLandPrefsStore = create<LandPrefsState>()(
  persist(
    (set, get) => ({
      enabledCategories: DEFAULT_ENABLED_CATEGORIES,
      lastLandCount: DEFAULT_LAND_COUNT,
      selectedStaples: [],

      setEnabledCategories: (cats) => {
        // Always ensure 'basic' is present
        const withBasic: LandCategory[] = cats.includes('basic') ? cats : [...cats, 'basic'];
        set({ enabledCategories: withBasic });
      },

      toggleCategory: (cat) => {
        // 'basic' cannot be disabled
        if (cat === 'basic') return;

        const { enabledCategories } = get();
        const isEnabled = enabledCategories.includes(cat);

        if (isEnabled) {
          set({ enabledCategories: enabledCategories.filter((c) => c !== cat) });
        } else {
          set({ enabledCategories: [...enabledCategories, cat] });
        }
      },

      setLastLandCount: (n) => {
        const clamped = Math.max(1, n);
        set({ lastLandCount: clamped });
      },

      setSelectedStaples: (names) => {
        set({ selectedStaples: names });
      },
    }),
    {
      name: 'mtg-deck-tool:land-prefs',
    },
  ),
);
